const TESSERACT_VERSION = "7.0.0";
const TESSERACT_SCRIPT_URL = `https://cdn.jsdelivr.net/npm/tesseract.js@${TESSERACT_VERSION}/dist/tesseract.min.js`;

let tesseractLoaderPromise = null;

function cleanText(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function titleCase(value) {
  return cleanText(value)
    .toLocaleLowerCase("it")
    .replace(/(^|[\s'’-])([\p{L}])/gu, (match, separator, letter) => `${separator}${letter.toLocaleUpperCase("it")}`);
}

function normalizeOcrText(value) {
  return String(value || "")
    .replace(/\r/g, "\n")
    .replace(/[|¦]/g, "I")
    .replace(/[“”]/g, '"')
    .replace(/[’]/g, "'");
}

function ocrLines(text) {
  return normalizeOcrText(text)
    .split(/\n+/)
    .map((line) => cleanText(line))
    .filter(Boolean);
}

function normalizeMrzLine(line) {
  return String(line || "")
    .toUpperCase()
    .replace(/[«‹]/g, "<")
    .replace(/\s+/g, "<")
    .replace(/[^A-Z0-9<]/g, "");
}

function mrzLines(text) {
  return ocrLines(text)
    .filter((line) => /[<«‹]/.test(line))
    .map(normalizeMrzLine)
    .filter((line) => line.includes("<") && line.length >= 25);
}

function mrzName(value) {
  return titleCase(String(value || "").replace(/<+/g, " "));
}

function parseMrzDate(value, kind) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 6);
  if (!/^\d{6}$/.test(digits)) return "";
  const yy = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const day = Number(digits.slice(4, 6));
  if (month < 1 || month > 12 || day < 1 || day > 31) return "";
  const currentYY = new Date().getFullYear() % 100;
  const year = kind === "expiry" ? 2000 + yy : yy > currentYY ? 1900 + yy : 2000 + yy;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return "";
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function parseVisibleDate(value) {
  const text = cleanText(value);
  const dayFirst = text.match(/\b(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{2,4})\b/);
  if (dayFirst) {
    let year = Number(dayFirst[3]);
    if (year < 100) year += year > (new Date().getFullYear() % 100) ? 1900 : 2000;
    const month = Number(dayFirst[2]);
    const day = Number(dayFirst[1]);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
  }
  const yearFirst = text.match(/\b(\d{4})[.\-/](\d{1,2})[.\-/](\d{1,2})\b/);
  if (yearFirst) {
    const year = Number(yearFirst[1]);
    const month = Number(yearFirst[2]);
    const day = Number(yearFirst[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
  }
  return "";
}

function extractLineValue(lines, patterns = []) {
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    for (const pattern of patterns) {
      const match = line.match(pattern);
      if (!match) continue;
      const inlineValue = cleanText(match[1]);
      if (inlineValue) return inlineValue;
      const nextLine = cleanText(lines[index + 1]);
      if (nextLine) return nextLine;
    }
  }
  return "";
}

function extractDateValue(lines, patterns = []) {
  const value = extractLineValue(lines, patterns);
  if (value) {
    const parsed = parseVisibleDate(value);
    if (parsed) return parsed;
  }
  for (let index = 0; index < lines.length; index += 1) {
    if (!patterns.some((pattern) => pattern.test(lines[index]))) continue;
    for (let offset = 0; offset <= 2; offset += 1) {
      const parsed = parseVisibleDate(lines[index + offset] || "");
      if (parsed) return parsed;
    }
  }
  return "";
}

function parseTd1(lines) {
  const candidates = lines.filter((line) => line.length >= 27 && line.length <= 35);
  for (let index = 0; index <= candidates.length - 3; index += 1) {
    const line1 = candidates[index].padEnd(30, "<").slice(0, 30);
    const line2 = candidates[index + 1].padEnd(30, "<").slice(0, 30);
    const line3 = candidates[index + 2].padEnd(30, "<").slice(0, 30);
    if (!line3.includes("<<") || !/^[A-Z0-9<]{2}[A-Z<]{3}/.test(line1)) continue;
    const nameParts = line3.split("<<");
    return {
      documentNumber: line1.slice(5, 14).replace(/</g, ""),
      issuingStateIso3: line1.slice(2, 5).replace(/</g, ""),
      birthDate: parseMrzDate(line2.slice(0, 6), "birth"),
      gender: ["M", "F"].includes(line2.charAt(7)) ? line2.charAt(7) : "",
      documentExpiryDate: parseMrzDate(line2.slice(8, 14), "expiry"),
      nationalityIso3: line2.slice(15, 18).replace(/</g, ""),
      surname: mrzName(nameParts[0]),
      name: mrzName(nameParts.slice(1).join(" ")),
      documentType: "Carta identità",
    };
  }
  return null;
}

function parseTd3(lines) {
  const candidates = lines.filter((line) => line.length >= 40 && line.length <= 48);
  for (let index = 0; index <= candidates.length - 2; index += 1) {
    const line1 = candidates[index].padEnd(44, "<").slice(0, 44);
    const line2 = candidates[index + 1].padEnd(44, "<").slice(0, 44);
    if (!line1.startsWith("P") || !line1.includes("<<")) continue;
    const nameParts = line1.slice(5).split("<<");
    return {
      documentNumber: line2.slice(0, 9).replace(/</g, ""),
      issuingStateIso3: line1.slice(2, 5).replace(/</g, ""),
      nationalityIso3: line2.slice(10, 13).replace(/</g, ""),
      birthDate: parseMrzDate(line2.slice(13, 19), "birth"),
      gender: ["M", "F"].includes(line2.charAt(20)) ? line2.charAt(20) : "",
      documentExpiryDate: parseMrzDate(line2.slice(21, 27), "expiry"),
      surname: mrzName(nameParts[0]),
      name: mrzName(nameParts.slice(1).join(" ")),
      documentType: "Passaporto",
    };
  }
  return null;
}

function extractFiscalCode(text) {
  const compact = normalizeOcrText(text).toUpperCase().replace(/[^A-Z0-9]/g, "");
  const match = compact.match(/[A-Z]{6}\d{2}[ABCDEHLMPRST]\d{2}[A-Z]\d{3}[A-Z]/);
  return match?.[0] || "";
}

function detectDocumentType(text, mrz) {
  const upper = normalizeOcrText(text).toUpperCase();
  if (/PERMESSO\s+DI\s+SOGGIORNO|RESIDENCE\s+PERMIT/.test(upper)) return "Permesso di soggiorno";
  if (/PATENTE\s+DI\s+GUIDA|DRIVING\s+LICEN[CS]E/.test(upper)) return "Patente";
  if (/PASSAPORTO|PASSPORT/.test(upper) || mrz?.documentType === "Passaporto") return "Passaporto";
  if (/CARTA\s+(?:D['’]?|DI\s*)IDENTIT|IDENTITY\s+CARD|CARTE\s+D['’]IDENTIT/.test(upper) || mrz?.documentType === "Carta identità") return "Carta identità";
  return mrz?.documentType || "";
}

function extractDocumentNumber(lines, mrz) {
  if (cleanText(mrz?.documentNumber)) return cleanText(mrz.documentNumber).replace(/[^A-Z0-9]/gi, "").toUpperCase();
  const value = extractLineValue(lines, [
    /(?:NUMERO\s+(?:DEL\s+)?DOCUMENTO|DOCUMENT\s*(?:NO|NUMBER)|DOC(?:UMENT)?\.?\s*NO|N[°º.])\s*[:\-]?\s*([A-Z0-9]{5,20})/i,
  ]);
  return cleanText(value).replace(/[^A-Z0-9]/gi, "").toUpperCase();
}

function extractResidence(lines) {
  const value = extractLineValue(lines, [
    /(?:RESIDENZA(?:\s*\/\s*RESIDENCE)?|RESIDENCE|ADDRESS|INDIRIZZO)\s*[:\-]?\s*(.*)$/i,
  ]);
  if (!value) return {};
  const text = cleanText(value);
  const postalCode = text.match(/\b\d{5}\b/)?.[0] || "";
  const province = text.match(/\(([A-Z]{2})\)/)?.[1] || "";
  const streetMatch = text.match(/\b(VIA|VIALE|PIAZZA|PIAZZALE|CORSO|VICOLO|STRADA|LARGO|CONTRADA|LOCALIT[AÀ]|LOC\.?|FRAZIONE|FRAZ\.?)\b/i);
  let city = "";
  let address = "";
  let streetNumber = "";
  if (streetMatch?.index >= 0) {
    const cityPart = cleanText(text.slice(0, streetMatch.index).replace(/\b\d{5}\b/g, "").replace(/\([A-Z]{2}\)/g, ""));
    city = cityPart.replace(/[,;\-]+$/g, "").trim();
    const streetPart = cleanText(text.slice(streetMatch.index));
    streetNumber = streetPart.match(/\b(\d+[A-Z]?(?:\/[A-Z0-9]+)?)\s*$/i)?.[1] || "";
    address = cleanText(streetPart.replace(streetNumber ? new RegExp(`${streetNumber.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*$`, "i") : /$^/, ""));
  }
  return { residenceText: text, address, streetNumber, city, postalCode, province };
}

function parseIdentityDocumentText(text) {
  const lines = ocrLines(text);
  const normalizedMrzLines = mrzLines(text);
  const mrz = parseTd3(normalizedMrzLines) || parseTd1(normalizedMrzLines) || {};

  const detectedDocumentType = detectDocumentType(text, mrz);
  const licenceSurname = detectedDocumentType === "Patente" ? extractLineValue(lines, [/^1[.)]?\s+(.+)$/i]) : "";
  const licenceName = detectedDocumentType === "Patente" ? extractLineValue(lines, [/^2[.)]?\s+(.+)$/i]) : "";
  const licenceBirth = detectedDocumentType === "Patente" ? extractLineValue(lines, [/^3[.)]?\s+(.+)$/i]) : "";
  const combinedBirth = extractLineValue(lines, [/(?:LUOGO\s+E\s+DATA\s+DI\s+NASCITA(?:\s*\/\s*PLACE\s+AND\s+DATE\s+OF\s+BIRTH)?|PLACE\s+AND\s+DATE\s+OF\s+BIRTH)\s*[:\-]?\s*(.*)$/i]);
  const surname = cleanText(mrz.surname) || titleCase(extractLineValue(lines, [/(?:COGNOME(?:\s*\/\s*SURNAME)?|SURNAME)\s*[:\-]?\s*(.*)$/i]) || licenceSurname);
  const name = cleanText(mrz.name) || titleCase(extractLineValue(lines, [/(?:^|\s)(?:NOME(?:\s*\/\s*NAME)?|GIVEN\s+NAMES?|NAME)\s*[:\-]?\s*(.*)$/i]) || licenceName);
  const birthPlaceRaw = extractLineValue(lines, [/(?:LUOGO\s+DI\s+NASCITA(?:\s*\/\s*PLACE\s+OF\s+BIRTH)?|PLACE\s+OF\s+BIRTH)\s*[:\-]?\s*(.*)$/i]) || combinedBirth || licenceBirth;
  const birthPlace = titleCase(cleanText(birthPlaceRaw).replace(/\b\d{1,2}[.\-/]\d{1,2}[.\-/]\d{2,4}\b/g, "").replace(/[,;\-]+$/g, ""));
  const nationalityText = cleanText(extractLineValue(lines, [/(?:CITTADINANZA(?:\s*\/\s*NATIONALITY)?|NATIONALITY)\s*[:\-]?\s*(.*)$/i]));
  const issuePlace = titleCase(extractLineValue(lines, [/(?:LUOGO\s+DI\s+RILASCIO(?:\s*\/\s*PLACE\s+OF\s+ISSUE)?|PLACE\s+OF\s+ISSUE)\s*[:\-]?\s*(.*)$/i]));
  const issuer = titleCase(extractLineValue(lines, [/(?:ENTE\s+RILASCIO|AUTORIT[AÀ](?:\s*\/\s*AUTHORITY)?|AUTHORITY|RILASCIAT[AO]\s+DA|ISSUED\s+BY)\s*[:\-]?\s*(.*)$/i]));
  const residence = extractResidence(lines);

  const visibleGender = extractLineValue(lines, [/(?:SESSO(?:\s*\/\s*SEX)?|SEX)\s*[:\-]?\s*([MF])/i]).toUpperCase();
  const birthDate = cleanText(mrz.birthDate) || parseVisibleDate(combinedBirth || licenceBirth) || extractDateValue(lines, [/(?:DATA\s+DI\s+NASCITA|DATE\s+OF\s+BIRTH|NATO\s+IL|NATA\s+IL)\s*[:\-]?\s*(.*)$/i]);
  const licenceIssueDate = detectedDocumentType === "Patente" ? parseVisibleDate(extractLineValue(lines, [/^4\s*A[.)]?\s+(.+)$/i, /^4A[.)]?\s+(.+)$/i])) : "";
  const licenceExpiryDate = detectedDocumentType === "Patente" ? parseVisibleDate(extractLineValue(lines, [/^4\s*B[.)]?\s+(.+)$/i, /^4B[.)]?\s+(.+)$/i])) : "";
  const documentIssueDate = licenceIssueDate || extractDateValue(lines, [/(?:DATA\s+(?:DI\s+)?RILASCIO|DATE\s+OF\s+ISSUE|EMISSIONE|ISSUING|ISSUED)\s*[:\-]?\s*(.*)$/i]);
  const documentExpiryDate = cleanText(mrz.documentExpiryDate) || licenceExpiryDate || extractDateValue(lines, [/(?:DATA\s+(?:DI\s+)?SCADENZA|DATE\s+OF\s+EXPIRY|SCADENZA|EXPIRY|VALID(?:O|A)?\s+FINO\s+AL|VALID\s+UNTIL)\s*[:\-]?\s*(.*)$/i]);

  return {
    customer: {
      surname,
      name,
      gender: cleanText(mrz.gender || visibleGender).toUpperCase(),
      birthDate,
      birthPlace,
      fiscalCode: extractFiscalCode(text),
      nationalityText,
      nationalityIso3: cleanText(mrz.nationalityIso3).toUpperCase(),
      ...residence,
    },
    document: {
      documentType: detectedDocumentType,
      documentNumber: detectedDocumentType === "Patente" ? (cleanText(extractLineValue(lines, [/^5[.)]?\s+([A-Z0-9]{5,20})$/i])).replace(/[^A-Z0-9]/gi, "").toUpperCase() || extractDocumentNumber(lines, mrz)) : extractDocumentNumber(lines, mrz),
      documentIssueCountryIso3: cleanText(mrz.issuingStateIso3).toUpperCase(),
      documentIssuePlace: issuePlace,
      documentIssueDate,
      documentExpiryDate,
      documentIssuer: issuer || (detectedDocumentType === "Patente" ? titleCase(extractLineValue(lines, [/^4\s*C[.)]?\s+(.+)$/i, /^4C[.)]?\s+(.+)$/i])) : ""),
    },
    rawText: cleanText(text),
  };
}

export function loadIdentityDocumentOcrEngine() {
  if (typeof window === "undefined") return Promise.reject(new Error("OCR non disponibile fuori dal browser."));
  if (window.Tesseract?.createWorker) return Promise.resolve(window.Tesseract);
  if (tesseractLoaderPromise) return tesseractLoaderPromise;

  tesseractLoaderPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-vesta-tesseract="${TESSERACT_VERSION}"]`);
    const finish = () => window.Tesseract?.createWorker ? resolve(window.Tesseract) : reject(new Error("Motore OCR non disponibile."));
    if (existing) {
      existing.addEventListener("load", finish, { once: true });
      existing.addEventListener("error", () => reject(new Error("Impossibile caricare il motore OCR.")), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = TESSERACT_SCRIPT_URL;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.referrerPolicy = "no-referrer";
    script.dataset.vestaTesseract = TESSERACT_VERSION;
    script.addEventListener("load", finish, { once: true });
    script.addEventListener("error", () => reject(new Error("Impossibile caricare il motore OCR.")), { once: true });
    document.head.appendChild(script);
  }).catch((error) => {
    tesseractLoaderPromise = null;
    throw error;
  });

  return tesseractLoaderPromise;
}

export async function recognizeIdentityDocumentImages(images = [], onProgress) {
  const validImages = (Array.isArray(images) ? images : []).filter(Boolean);
  if (!validImages.length) throw new Error("Acquisisci almeno un lato del documento.");

  const Tesseract = await loadIdentityDocumentOcrEngine();
  const worker = await Tesseract.createWorker(["ita", "eng"], 1, {
    logger: (message) => {
      if (message?.status === "recognizing text" && Number.isFinite(message.progress)) onProgress?.(message.progress);
    },
  });

  try {
    const texts = [];
    for (let index = 0; index < validImages.length; index += 1) {
      onProgress?.(index / validImages.length);
      const result = await worker.recognize(validImages[index]);
      texts.push(result?.data?.text || "");
      onProgress?.((index + 1) / validImages.length);
    }
    return parseIdentityDocumentText(texts.join("\n"));
  } finally {
    await worker.terminate();
  }
}

export { parseIdentityDocumentText };
